import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Inbox,
  UserPlus,
  Phone,
  Wallet,
  TrendingUp,
  Users,
  CheckCircle2,
  Building2,
  Handshake,
} from "lucide-react";
import { Link } from "react-router";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import StatusBadge from "../../components/dashboard/StatusBadge";
import RecoveryTrendChart from "../../components/dashboard/charts/RecoveryTrendChart";
import AgingBucketsChart from "../../components/dashboard/charts/AgingBucketsChart";
import CollectionFunnelChart from "../../components/dashboard/charts/CollectionFunnelChart";
import ChannelPerformanceChart from "../../components/dashboard/charts/ChannelPerformanceChart";
import RegionalCollectionsChart from "../../components/dashboard/charts/RegionalCollectionsChart";
import { formatCurrency } from "../../data/mock/dashboardData";
import useRole from "../../hooks/useRole";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import usePermissions from "../../hooks/usePermissions";
import { OPS_PERMISSIONS as P } from "../../config/permissions";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  clearOpsDashboardOverviewCache,
  fetchOpsDashboardOverview,
  fetchOpsDashboardReport,
} from "../../services/dashboardApi";
import { showError } from "../../utils/toast";

function Panel({ children, className = "" }) {
  return (
    <div
      className={`flex h-full flex-col rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)] ${className}`}
    >
      {children}
    </div>
  );
}

function PanelHeader({ title, subtitle, action }) {
  return (
    <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-bold text-primary">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-primary/40">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function DashboardHome() {
  const { user, token } = useSelector((state) => state.auth);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const { isCallCenterAgent, isCallCenterManager, isSuperAdmin, isExecutiveManager, isLegalOfficer } =
    useRole();
  const isOrgWide = isSuperAdmin || isExecutiveManager;
  const { hasPermission } = usePermissions();
  const canSeeInstitutions = hasPermission(P.INSTITUTIONS_VIEW);
  const [overview, setOverview] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(useLiveApi);
  const liveDashTick = useLiveEventTick(/^(portfolio\.|case\.|payment\.)/);

  useEffect(() => {
    if (!useLiveApi) {
      setLoadingOverview(false);
      return undefined;
    }

    let cancelled = false;
    const quiet = Boolean(liveDashTick);
    if (!quiet) setLoadingOverview(true);
    if (quiet) clearOpsDashboardOverviewCache();

    Promise.all([
      fetchOpsDashboardOverview(token),
      fetchOpsDashboardReport(token, { force: quiet }),
    ])
      .then(([overviewData, reportData]) => {
        if (!cancelled) {
          setOverview(overviewData || null);
          setReport(reportData || null);
        }
      })
      .catch((error) => {
        if (!cancelled) showError(error.message || "Could not load dashboard summary.");
      })
      .finally(() => {
        if (!cancelled) setLoadingOverview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, liveDashTick]);

  const paymentTrend = (() => {
    const rows = Array.isArray(report?.payments_by_day)
      ? report.payments_by_day
      : Array.isArray(report?.payments_last_30_days)
        ? report.payments_last_30_days
        : [];
    if (!rows.length) return null;
    return {
      labels: rows.map((r) => String(r.day || "").slice(5)),
      collected: rows.map((r) => Number(r.amount) || 0),
    };
  })();

  const statusStages = (() => {
    const byStatus = report?.cases_by_status;
    if (!byStatus || typeof byStatus !== "object") return null;
    return Object.entries(byStatus).map(([label, count]) => ({
      label: String(label),
      count: Number(count) || 0,
    }));
  })();

  const regionItems = (() => {
    const rows = Array.isArray(report?.collections_by_region) ? report.collections_by_region : [];
    if (!rows.length) return null;
    return rows.map((r) => ({
      name: r.label || r.name || "Unknown",
      amount: Number(r.collected ?? r.outstanding ?? r.amount) || 0,
      accounts: Number(r.total ?? r.accounts) || 0,
    }));
  })();

  const channels = (Array.isArray(report?.channels) ? report.channels : []).map((row) => ({
    name: row.channel || row.name || "Channel",
    total: Number(row.total) || 0,
  }));

  const kpis = {
    casesInQueue: Number(overview?.cases_in_queue ?? overview?.unassigned_cases ?? 0),
    unassignedCases: Number(overview?.unassigned_cases ?? 0),
    assignedToday: Number(overview?.assigned_today ?? 0),
    activeAgents: Number(overview?.active_agents ?? 0),
    contactAttemptsToday: Number(overview?.contact_attempts_today ?? 0),
    recoveryToday: Number(overview?.recovery_today ?? 0),
    escalationsPending: Number(overview?.escalations_pending ?? 0),
    avgCasesPerAgent: Number(overview?.avg_cases_per_agent ?? 0),
    institutionsCount: Number(overview?.institutions ?? overview?.institutions_count ?? 0),
    scopeLabel:
      overview?.scope === "organization"
        ? "All institutions"
        : overview?.scope === "assigned_cases"
          ? "Your assigned cases"
          : overview?.scope === "legal_cases"
            ? "Your legal cases"
            : "Your assigned institutions",
    loanAmount: Number(overview?.loan_amount ?? 0),
    totalPaid: Number(overview?.total_paid ?? 0),
    engagementsToday: Number(overview?.engagements_today ?? 0),
    paymentsToday: Number(overview?.payments_today ?? 0),
    outstanding: Number(overview?.outstanding ?? 0),
    openCases: Number(overview?.open_cases ?? 0),
    closedCases: Number(overview?.closed_cases ?? 0),
  };

  const insights = [
    {
      icon: Users,
      title: `${kpis.activeAgents} active operators`,
      detail: `Avg ${kpis.avgCasesPerAgent} cases each · ${kpis.assignedToday} assigned today`,
    },
    {
      icon: Building2,
      title: `${kpis.institutionsCount} client institutions`,
      detail: `${formatCurrency(kpis.outstanding)} outstanding across books`,
    },
    {
      icon: Handshake,
      title: `${formatCurrency(kpis.recoveryToday)} recovered today`,
      detail: `${formatCurrency(kpis.outstanding)} outstanding across books`,
    },
  ];

  const primaryCtaHref = isCallCenterManager || canSeeInstitutions
    ? "/dashboard/institutions"
    : "/dashboard/cases";
  const primaryCtaLabel = isCallCenterManager || canSeeInstitutions ? "Open institutions" : "View cases";

  const queuePreview = Array.isArray(overview?.queue_preview) ? overview.queue_preview : [];
  const institutionsList = Array.isArray(overview?.institutions_preview)
    ? overview.institutions_preview
    : [];

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="mx-auto max-w-8xl px-4 pb-10 pt-5 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 border-b border-primary/[0.06] pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium text-primary/40">SimCollect Operations</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-primary sm:text-[1.75rem]">
            {greeting},{" "}
            <span className="text-primary/80">{user?.name?.split(/\s+/)[0] ?? "there"}</span>
          </h1>
          <p className="mt-1.5 max-w-lg text-sm text-primary/45">
            {loadingOverview
              ? "Loading summary…"
              : `${kpis.scopeLabel} · ${kpis.casesInQueue || kpis.openCases} open cases · ${kpis.engagementsToday || kpis.contactAttemptsToday} engagements today · ${kpis.paymentsToday} payments logged today`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={primaryCtaHref}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-light"
          >
            <Inbox className="h-3.5 w-3.5" />
            {primaryCtaLabel}
          </Link>
        </div>
      </div>

      <StatCardGrid cols={4} className="mt-4">
        <StatCard
          label={isCallCenterAgent ? "Your open cases" : isLegalOfficer ? "Your legal cases" : "Cases in queue"}
          value={loadingOverview ? "…" : String(kpis.casesInQueue || kpis.openCases)}
          sub={isOrgWide || isCallCenterManager ? `${kpis.unassignedCases} unassigned` : "In your book"}
          icon={Inbox}
          tone="primary"
        />
        {isOrgWide || isCallCenterManager ? (
          <StatCard
            label="Assigned today"
            value={loadingOverview ? "…" : String(kpis.assignedToday)}
            sub={isOrgWide ? `${kpis.activeAgents} active operators` : `${kpis.activeAgents} agents on your book`}
            icon={UserPlus}
            tone="accent"
          />
        ) : (
          <StatCard
            label="Loan book"
            value={loadingOverview ? "…" : formatCurrency(kpis.loanAmount)}
            sub="Loan amount on your cases"
            icon={Wallet}
            tone="accent"
          />
        )}
        <StatCard
          label="Contact attempts"
          value={loadingOverview ? "…" : String(kpis.contactAttemptsToday)}
          sub={isCallCenterAgent ? "Your interactions today" : "Logged interactions today"}
          icon={Phone}
          tone="emerald"
        />
        <StatCard
          label={isOrgWide ? "Recovery today" : "Total paid"}
          value={loadingOverview ? "…" : formatCurrency(isOrgWide ? kpis.recoveryToday : kpis.totalPaid)}
          sub={isOrgWide ? "Payments collected today" : "Paid on your cases"}
          icon={Wallet}
          tone="emerald"
        />
      </StatCardGrid>

      {isOrgWide ? (
        <StatCardGrid cols={3} className="mt-2">
          <StatCard
            label="Avg cases per agent"
            value={loadingOverview ? "…" : String(kpis.avgCasesPerAgent)}
            sub="Active caseload balance"
            icon={Users}
            tone="primary"
          />
          <StatCard
            label="Outstanding"
            value={loadingOverview ? "…" : formatCurrency(kpis.outstanding)}
            sub="Across all institutions"
            icon={TrendingUp}
            tone="accent"
          />
          <StatCard
            label="Open cases"
            value={loadingOverview ? "…" : String(kpis.openCases)}
            sub={`${kpis.closedCases} closed`}
            icon={CheckCircle2}
            tone="emerald"
          />
        </StatCardGrid>
      ) : (
        <StatCardGrid cols={3} className="mt-2">
          <StatCard
            label="Outstanding"
            value={loadingOverview ? "…" : formatCurrency(kpis.outstanding)}
            sub="Your book"
            icon={TrendingUp}
            tone="accent"
          />
          <StatCard
            label="Open cases"
            value={loadingOverview ? "…" : String(kpis.openCases)}
            sub={`${kpis.closedCases} closed`}
            icon={CheckCircle2}
            tone="emerald"
          />
          {isCallCenterManager ? (
            <StatCard
              label="Institutions"
              value={loadingOverview ? "…" : String(kpis.institutionsCount)}
              sub="Assigned to you"
              icon={Building2}
              tone="primary"
            />
          ) : (
            <StatCard
              label="Payments today"
              value={loadingOverview ? "…" : String(kpis.paymentsToday)}
              sub="On your cases"
              icon={Wallet}
              tone="primary"
            />
          )}
        </StatCardGrid>
      )}

      {isOrgWide ? (
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {insights.map((item) => (
          <div
            key={item.title}
            className="flex gap-3 rounded-xl border border-primary/[0.07] bg-gradient-to-br from-primary/[0.03] to-accent/[0.06] px-4 py-3.5"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
              <item.icon className="h-4 w-4 text-primary" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-bold text-primary">{item.title}</p>
              <p className="mt-0.5 text-[11px] text-primary/45">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-12">
        <Panel className="lg:col-span-8">
          <PanelHeader
            title="Recovery (30 days)"
            subtitle={
              loadingOverview
                ? "Loading payments…"
                : paymentTrend
                  ? "Payments collected from live dashboard report"
                  : "No payments in this period yet"
            }
          />
          <RecoveryTrendChart
            labels={paymentTrend?.labels}
            collected={paymentTrend?.collected}
            subtitle={paymentTrend ? "Last 30 days" : undefined}
          />
        </Panel>

        <Panel className="lg:col-span-4">
          <PanelHeader title="Overdue balances" subtitle="Grouped by how long payments are late" />
          <AgingBucketsChart buckets={report?.aging_buckets} />
        </Panel>
      </div>

      <div className="mt-4 grid items-stretch gap-4 lg:grid-cols-12">
        <Panel className="lg:col-span-7">
          <PanelHeader title="Cases by status" subtitle="Live case distribution" />
          <CollectionFunnelChart stages={statusStages || undefined} />
        </Panel>

        <Panel className="lg:col-span-5">
          <PanelHeader title="Outreach channel performance" subtitle="Contact volume by channel" />
          <ChannelPerformanceChart channels={channels} />
        </Panel>
      </div>

      {isOrgWide || isCallCenterManager ? (
      <div className="mt-4 grid items-stretch gap-4 lg:grid-cols-12">
        <Panel className={isOrgWide ? "lg:col-span-7" : "lg:col-span-12"}>
          <PanelHeader
            title="Institution performance"
            subtitle="Active cases and exposure by client institution"
            action={
              canSeeInstitutions && (
                <Link
                  to="/dashboard/institutions"
                  className="text-xs font-semibold text-accent-dark hover:text-primary"
                >
                  Manage →
                </Link>
              )
            }
          />
          <ul className="space-y-3">
            {institutionsList.length === 0 ? (
              <li className="rounded-lg bg-primary/[0.02] px-3 py-4 text-xs text-primary/45">
                {loadingOverview
                  ? "Loading institution performance…"
                  : "No institution case data yet."}
              </li>
            ) : (
              institutionsList.map((inst) => (
                <li
                  key={inst.id || inst.code || inst.name}
                  className="rounded-lg border border-primary/[0.06] px-3 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-primary">{inst.name}</p>
                      {inst.code ? (
                        <p className="mt-0.5 truncate font-mono text-[10px] font-semibold tracking-wide text-primary/45">
                          {inst.code}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <StatusBadge status={inst.status || "active"} />
                      <p className="text-sm font-bold text-primary">{formatCurrency(inst.outstanding)}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-primary/45">
                    {inst.activeCases} active cases
                    {inst.region && inst.region !== "—" ? ` · ${inst.region}` : ""}
                  </p>
                </li>
              ))
            )}
          </ul>
        </Panel>

        {isOrgWide ? (
        <Panel className="flex lg:col-span-5">
          <PanelHeader title="Collections by region" subtitle="Where money is coming from" />
          <RegionalCollectionsChart regions={regionItems || undefined} />
        </Panel>
        ) : null}
      </div>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-12">
        <Panel className="lg:col-span-12">
          <PanelHeader
            title={isCallCenterAgent || isLegalOfficer ? "Your open cases" : isCallCenterManager ? "Cases on your institutions" : "Recent cases"}
            subtitle={
              isCallCenterAgent
                ? "Cases assigned to you awaiting action"
                : isLegalOfficer
                  ? "Escalations assigned to you"
                  : isCallCenterManager
                    ? "Latest cases on institutions assigned to you"
                    : "Latest recovery cases across institutions"
            }
            action={
              <Link
                to={isOrgWide || isCallCenterManager ? "/dashboard/institutions" : "/dashboard/cases"}
                className="text-xs font-semibold text-accent-dark hover:text-primary"
              >
                View all →
              </Link>
            }
          />
          <ul className="space-y-2">
            {queuePreview.length === 0 && (
              <li className="text-xs text-primary/40">
                {loadingOverview ? "Loading cases…" : "Nothing here right now."}
              </li>
            )}
            {queuePreview.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-3 rounded-lg border border-primary/[0.05] bg-primary/[0.015] px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-primary">{c.customer}</p>
                  <p className="text-[10px] text-primary/40">
                    {c.id} · {c.institution} · {c.daysOverdue} days late
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-primary">{formatCurrency(c.amountOwed)}</p>
                  <StatusBadge
                    status={c.priority === "Urgent" ? "overdue" : "pending"}
                    label={c.priority}
                  />
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
